/**
 * Plan-based feature entitlements system.
 *
 * Maps Stripe product IDs to plan tiers, and plan tiers to feature access.
 * Primary gating uses `plan_id` from `org_billing` (synced by webhooks).
 * No Stripe API calls needed: all lookups are local.
 *
 * ## Adding a new gated feature
 *
 * This module is the **client-side mirror** of the canonical SQL
 * `plan_has_feature(tier, feature_key)` function. The two MUST stay in
 * lockstep, or the UI badge state and DB enforcement will disagree.
 *
 * Checklist when adding a `FeatureKey`:
 *
 * 1. Add the key to `FeatureKey` and to every tier in `PLAN_ENTITLEMENTS`.
 * 2. Add UI copy to `lib/featureCatalog.ts` (`FEATURE_CATALOG`) so
 *    `FeatureGateBadge` and `FeatureUpgradeModal` can render it.
 * 3. Migration: register the key inside `plan_has_feature()` in the
 *    matching tier bucket. The function comment marks this file as the
 *    source of truth.
 * 4. DB enforcement on `interview_sessions`: do NOT add a parallel
 *    trigger. Extend the single canonical
 *    `validate_interview_session_entitlements()` function (see
 *    `20260508120000_add_probing_depth.sql` for the template):
 *      a. Add the new check inside the existing body.
 *      b. Add the column to the legacy ownership-claim short-circuit so
 *         pre-org rows can be attached to an org without retroactive
 *         rejection.
 *      c. Raise `feature_not_enabled:<key>` with `ERRCODE = 'P0001'` and
 *         an upgrade `HINT`. This is the format API handlers parse.
 *      d. Extend the trigger column list (DROP + CREATE TRIGGER with the
 *         full `BEFORE INSERT OR UPDATE OF ...` list) to include the
 *         new column.
 *    For features stored on other tables, apply the same pattern:
 *    extend that table's existing entitlement trigger rather than
 *    forking.
 *
 * @module lib/entitlements
 */

import {
  resolvePlanTier as resolveConfiguredPlanTier,
  type PlanTier as ResolvablePlanTier,
} from './planConfig';

/**
 * Supported plan tiers. Derived from the Stripe-resolvable tiers in
 * `lib/planConfig.ts` plus 'enterprise', which mirrors the SQL
 * `plan_has_feature` tier bucket and is never resolved from a Stripe
 * product ID.
 */
export type PlanTier = ResolvablePlanTier | 'enterprise';

/** All gated feature keys. */
export type FeatureKey =
  | 'audio_interviews'
  | 'video_interviews'
  | 'screen_sharing'
  | 'basic_analytics'
  | 'advanced_analytics'
  | 'custom_branding'
  | 'remove_holito_referral'
  | 'multi_language_interviews'
  | 'extended_key_topics'
  | 'embed_widget'
  | 'completion_redirect'
  | 'api_access'
  | 'auto_recipes';

/** Feature entitlement record - true means the feature is available. */
export type Entitlements = Record<FeatureKey, boolean>;

/** Legacy numeric-limit shape retained for existing callers. */
export interface PlanLimits {
  /** Shared technical step ceiling; no commercial topic allowance. */
  maxKeyTopics: number;
}

/** Interview-language policy for a plan tier. */
export interface InterviewLanguagePolicy {
  /** Whether respondents may freely choose their interview language. */
  allowLanguageChoice: boolean;
  /** Enforced interview language when free choice is not allowed. */
  enforcedLanguage: string | null;
}

/** Feature access per plan tier. */
const PLAN_ENTITLEMENTS: Record<PlanTier, Entitlements> = {
  free: {
    audio_interviews: false,
    video_interviews: false,
    screen_sharing: false,
    basic_analytics: true,
    advanced_analytics: false,
    custom_branding: false,
    remove_holito_referral: false,
    multi_language_interviews: true,
    extended_key_topics: true,
    embed_widget: true,
    completion_redirect: false,
    api_access: false,
    auto_recipes: false,
  },
  pro: {
    audio_interviews: true,
    video_interviews: true,
    screen_sharing: false,
    basic_analytics: true,
    advanced_analytics: false,
    custom_branding: true,
    remove_holito_referral: false,
    multi_language_interviews: true,
    extended_key_topics: true,
    embed_widget: true,
    completion_redirect: true,
    api_access: true,
    auto_recipes: true,
  },
  business: {
    audio_interviews: true,
    video_interviews: true,
    screen_sharing: true,
    basic_analytics: true,
    advanced_analytics: true,
    custom_branding: true,
    remove_holito_referral: true,
    multi_language_interviews: true,
    extended_key_topics: true,
    embed_widget: true,
    completion_redirect: true,
    api_access: true,
    auto_recipes: true,
  },
  enterprise: {
    audio_interviews: true,
    video_interviews: true,
    screen_sharing: true,
    basic_analytics: true,
    advanced_analytics: true,
    custom_branding: true,
    remove_holito_referral: true,
    multi_language_interviews: true,
    extended_key_topics: true,
    embed_widget: true,
    completion_redirect: true,
    api_access: true,
    auto_recipes: true,
  },
};

/** Shared technical ceiling on all tiers, including legacy topic callers. */
const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  free: { maxKeyTopics: 200 },
  pro: { maxKeyTopics: 200 },
  business: { maxKeyTopics: 200 },
  enterprise: { maxKeyTopics: 200 },
};

/**
 * Resolves a Stripe product ID to a plan tier.
 * Returns 'free' for null/undefined plan ids. Unknown non-null product IDs
 * also resolve to 'free' (fail-closed); the underlying resolver captures
 * them to PostHog instead of throwing.
 */
export function resolvePlanTier(planId: string | null | undefined): PlanTier {
  return resolveConfiguredPlanTier(planId);
}

/**
 * Checks whether a specific feature is available on a given plan tier.
 */
export function hasFeature(tier: PlanTier, feature: FeatureKey): boolean {
  return PLAN_ENTITLEMENTS[tier][feature];
}

/**
 * Returns the full entitlement set for a plan tier.
 */
export function getEntitlementsForPlan(tier: PlanTier): Entitlements {
  return { ...PLAN_ENTITLEMENTS[tier] };
}

/**
 * Returns numeric limits for a plan tier.
 */
export function getPlanLimits(tier: PlanTier): PlanLimits {
  return { ...PLAN_LIMITS[tier] };
}

/**
 * Returns the interview-language policy for a plan tier.
 * All standard tiers support choosing a conversation language.
 */
export function getInterviewLanguagePolicy(tier: PlanTier): InterviewLanguagePolicy {
  if (hasFeature(tier, 'multi_language_interviews')) {
    return {
      allowLanguageChoice: true,
      enforcedLanguage: null,
    };
  }

  return {
    allowLanguageChoice: false,
    enforcedLanguage: 'en-US',
  };
}

/**
 * Returns the minimum plan display name required for a feature,
 * or null if the feature is available on all plans.
 */
export function getMinimumPlan(feature: FeatureKey): string | null {
  // If free has it, it's available to all
  if (PLAN_ENTITLEMENTS.free[feature]) return null;
  // If pro has it, minimum is Pro
  if (PLAN_ENTITLEMENTS.pro[feature]) return 'Pro';
  // Otherwise it's Business+
  return 'Business';
}
